import { useState, useEffect, useCallback } from 'react'
import { supabase, DbArticle } from '../lib/supabase'

// App-level Article type that components use
export interface Article {
  id: string
  title: string
  content: string
  excerpt: string
  url: string
  savedAt: string
  readingTime: number
  tags: string[]
  readingProgress: number
  isFavorite: boolean
  isArchived: boolean
}

// Convert database article to app article
function dbToArticle(db: DbArticle): Article {
  // Estimate reading time from word count (avg 200 words/min)
  const readingTime = db.word_count ? Math.ceil(db.word_count / 200) : 5

  return {
    id: db.id,
    title: db.title || 'Untitled',
    content: db.content || '',
    excerpt: db.excerpt || '',
    url: db.url,
    savedAt: db.created_at,
    readingTime,
    tags: [], // Tags come from a separate table, could be joined later
    readingProgress: db.reading_progress,
    isFavorite: db.is_favorite,
    isArchived: db.is_archived,
  }
}

interface UseArticlesReturn {
  articles: Article[]
  loading: boolean
  error: Error | null
  refreshArticles: () => Promise<void>
  getArticle: (id: string) => Promise<Article | null>
  updateReadingProgress: (id: string, progress: number) => Promise<void>
  toggleFavorite: (id: string) => Promise<void>
  archiveArticle: (id: string) => Promise<void>
}

export function useArticles(userId: string | undefined): UseArticlesReturn {
  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchArticles = useCallback(async () => {
    if (!userId) {
      setArticles([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: queryError } = await supabase
        .from('articles')
        .select('*')
        .eq('user_id', userId)
        .eq('is_archived', false)
        .order('created_at', { ascending: false })

      if (queryError) {
        throw queryError
      }

      const mapped = (data as DbArticle[]).map(dbToArticle)
      setArticles(mapped)
    } catch (err) {
      console.error('Error fetching articles:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch articles'))
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchArticles()
  }, [fetchArticles])

  const getArticle = useCallback(async (id: string): Promise<Article | null> => {
    if (!userId) return null

    try {
      const { data, error: queryError } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .single()

      if (queryError) {
        throw queryError
      }

      return data ? dbToArticle(data as DbArticle) : null
    } catch (err) {
      console.error('Error fetching article:', err)
      return null
    }
  }, [userId])

  const updateReadingProgress = useCallback(async (id: string, progress: number) => {
    if (!userId) return

    try {
      const { error: updateError } = await supabase
        .from('articles')
        .update({ reading_progress: progress, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', userId)

      if (updateError) {
        throw updateError
      }

      // Update local state
      setArticles(prev => prev.map(article =>
        article.id === id ? { ...article, readingProgress: progress } : article
      ))
    } catch (err) {
      console.error('Error updating reading progress:', err)
    }
  }, [userId])

  const toggleFavorite = useCallback(async (id: string) => {
    if (!userId) return

    const article = articles.find(a => a.id === id)
    if (!article) return

    const newFavorite = !article.isFavorite

    try {
      const { error: updateError } = await supabase
        .from('articles')
        .update({ is_favorite: newFavorite, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', userId)

      if (updateError) {
        throw updateError
      }

      // Update local state
      setArticles(prev => prev.map(a =>
        a.id === id ? { ...a, isFavorite: newFavorite } : a
      ))
    } catch (err) {
      console.error('Error toggling favorite:', err)
    }
  }, [userId, articles])

  const archiveArticle = useCallback(async (id: string) => {
    if (!userId) return

    try {
      const { error: updateError } = await supabase
        .from('articles')
        .update({
          is_archived: true,
          archived_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', userId)

      if (updateError) {
        throw updateError
      }

      // Remove from local state (since we're filtering out archived)
      setArticles(prev => prev.filter(a => a.id !== id))
    } catch (err) {
      console.error('Error archiving article:', err)
    }
  }, [userId])

  return {
    articles,
    loading,
    error,
    refreshArticles: fetchArticles,
    getArticle,
    updateReadingProgress,
    toggleFavorite,
    archiveArticle,
  }
}
